<template>
  <div class="form-card">
    <div class="form-card__header">
      <h6 class="form-card__title">
        <i class="bi bi-funnel"></i>
        Filtros y porcentaje de incremento
      </h6>
    </div>

    <div class="form-card__body">
      <div class="form-card__field">
        <label class="form-card__label" for="ap-pricelist">Lista de precios</label>
        <q-select v-model="filters.priceList_id" :options="priceListOptions" placeholder="Seleccioná una lista" />
      </div>

      <div v-if="!isPriceListSelected" class="alert alert-info d-flex align-items-start mb-3" style="font-size: 12px;">
        <i class="bi bi-info-circle mr-2 mt-1"></i>
        <div>Elegí una <strong>lista de precios</strong> para habilitar los filtros.</div>
      </div>

      <div class="form-card__field">
        <label class="form-card__label" for="ap-category">Categoría</label>
        <q-select v-model="filters.category_id" :options="categoryOptions" :disabled="!isPriceListSelected" />
      </div>

      <div class="form-card__field">
        <label class="form-card__label" for="ap-provider">Proveedor</label>
        <q-select v-model="filters.provider_id" :options="providerOptions" :disabled="!isPriceListSelected" />
      </div>

      <div class="form-card__field">
        <label class="form-card__label" for="ap-brand">Marca</label>
        <q-select v-model="filters.brand_id" :options="brandOptions" :disabled="!isPriceListSelected" />
      </div>

      <div v-if="isPriceListSelected && !hasRestrictiveFilter" class="alert alert-warning d-flex align-items-start mb-3" style="font-size: 12px;">
        <i class="bi bi-info-circle mr-2 mt-1"></i>
        <div>Seleccioná al menos un filtro de <strong>categoría</strong>, <strong>proveedor</strong> o <strong>marca</strong> para habilitar el porcentaje.</div>
      </div>

      <div v-if="isPriceListSelected" class="alert alert-secondary d-flex align-items-center mb-3" style="font-size: 12px;">
        <i class="bi bi-box-seam mr-2"></i>
        <div>Productos a impactar: <strong>{{ filteredCount }}</strong></div>
      </div>

      <div class="form-card__field">
        <label class="form-card__label" for="ap-percentage">Porcentaje de incremento</label>
        <input
          id="ap-percentage"
          v-model.number="increase.percentage"
          type="number"
          step="0.01"
          class="form-control"
          placeholder="0"
          :disabled="!canApplyIncrease"
        >
      </div>

      <div class="form-card__field">
        <label class="form-card__label" for="ap-multiple">Múltiplo de redondeo</label>
        <input
          id="ap-multiple"
          v-model.number="increase.multiple"
          type="number"
          min="2"
          class="form-control"
          placeholder="10"
          :disabled="!canApplyIncrease"
        >
        <div class="form-card__hint">Múltiplo mínimo 2. Ej: 10 redondea al múltiplo de 10 más cercano.</div>
      </div>
    </div>

    <div class="form-card__footer">
      <button
        type="button"
        class="form-card__btn form-card__btn--primary"
        :disabled="!canApplyIncrease || isSubmitting"
        @click="actualizarPrecios"
      >
        <i class="bi bi-arrow-repeat"></i>
        Actualizar precios
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import QSelect from '@/components/shared/QSelect.vue';

export default {
  components: { QSelect },
  props: {
    filteredCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      filters: {
        priceList_id: '',
        category_id: 'all',
        provider_id: 'all',
        brand_id: 'all',
      },
      increase: {
        category_id: null,
        provider_id: null,
        brand_id: null,
        priceList_id: null,
        percentage: 0,
        multiple: 10,
      },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState('categories', { categories: state => state.categories }),
    ...mapState('brands', { brands: state => state.brands }),
    ...mapState('providers', { providers: state => state.providers }),
    ...mapState('priceLists', ['priceLists']),
    isPriceListSelected() {
      return this.filters.priceList_id !== '' && this.filters.priceList_id !== null && this.filters.priceList_id !== undefined;
    },
    hasRestrictiveFilter() {
      // priceList sola no cuenta: actualizaría todos los productos de la lista,
      // que es exactamente lo que queremos evitar.
      return this.filters.category_id !== 'all'
        || this.filters.provider_id !== 'all'
        || this.filters.brand_id !== 'all';
    },
    canApplyIncrease() {
      return this.isPriceListSelected && this.hasRestrictiveFilter;
    },
    priceListOptions() {
      return [{ value: 0, label: 'COSTO' }, ...this.priceLists.map(pl => ({ value: pl.id, label: pl.name }))];
    },
    categoryOptions() {
      return [{ value: 'all', label: 'Todas' }, ...this.categories.map(c => ({ value: c.id, label: c.name }))];
    },
    providerOptions() {
      return [{ value: 'all', label: 'Todos' }, ...this.providers.map(p => ({ value: p.id, label: p.name }))];
    },
    brandOptions() {
      return [{ value: 'all', label: 'Todas' }, ...this.brands.map(b => ({ value: b.id, label: b.name }))];
    },
  },
  watch: {
    'filters.priceList_id'(_newVal, oldVal) {
      // Si se limpia la lista, reseteamos los filtros dependientes
      if (!this.isPriceListSelected && oldVal !== '') {
        this.filters.category_id = 'all';
        this.filters.provider_id = 'all';
        this.filters.brand_id = 'all';
        this.increase.percentage = 0;
        this.increase.multiple = 10;
      }
    },
    filters: {
      handler() {
        this.increase.category_id = this.filters.category_id;
        this.increase.provider_id = this.filters.provider_id;
        this.increase.brand_id = this.filters.brand_id;
        this.increase.priceList_id = this.filters.priceList_id;
        if (this.isPriceListSelected) {
          this.$emit('filters', this.filters);
        } else {
          this.$emit('filters-cleared');
        }
      },
      deep: true,
    },
    'increase.percentage'() {
      this.emitIncreaseDebounced();
    },
    'increase.multiple'() {
      this.emitIncreaseDebounced();
    },
  },
  created() {
    this.$store.dispatch('categories/getCategories');
    this.$store.dispatch('brands/getBrands');
    this.$store.dispatch('providers/getProviders');
    this.$store.dispatch('priceLists/getPriceLists');
  },
  methods: {
    emitIncreaseDebounced() {
      clearTimeout(this._increaseTimer);
      this._increaseTimer = setTimeout(() => {
        this.$emit('increase-change', this.increase);
      }, 400);
    },
    resetForm() {
      this.filters.priceList_id = '';
      this.filters.category_id = 'all';
      this.filters.provider_id = 'all';
      this.filters.brand_id = 'all';
      this.increase.percentage = 0;
      this.increase.multiple = 10;
    },
    actualizarPrecios() {
      if (!this.isPriceListSelected) {
        alert("Tenés que elegir primero una lista de precios.");
        return;
      }
      if (!this.hasRestrictiveFilter) {
        alert("Por seguridad, debés seleccionar al menos un filtro de categoría, proveedor o marca. La lista de precios sola no alcanza.");
        return;
      }
      if (this.filteredCount <= 0) {
        alert("No hay productos que coincidan con los filtros aplicados.");
        return;
      }
      if (!confirm(`Se van a actualizar ${this.filteredCount} producto/s. Los cambios son irreversibles. ¿Desea continuar?`)) {
        return;
      }
      this.isSubmitting = true;
      this.$http.post('/api/products/updateprices', this.increase)
        .then((res) => {
          if (res.data.success) {
            alert("Precios actualizados correctamente.");
            this.resetForm();
            this.$emit('confirmsave');
          }
        })
        .catch((error) => {
          alert(error);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
  },
};
</script>
